import Layout from 'components/layout';

import React from 'react';

import { MarketoForm, i18nFunct, Link, LucidContainer } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Card, Heading, Paper } from 'lucid-components-react';
import {
  Drupal_NodeCaseStudy,
  Drupal_NodeSingleMarketoForm,
  Drupal_ParagraphMarketoForm
} from 'generated/drupal-graphql';

import { useMenuSingleMarketoFormPageMe } from 'hooks/use-menu';
import { backgroundGrayPattern } from 'theme/shared';
import { Text } from 'theme/typography';
import { ExtraData, TemplateProps, LucidTheme } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getMarketoForm } from 'utils/helpers';
import { mqMax } from 'theme/base';

const helpCenterLinks: { [key: string]: string } = {
  en: 'https://lucidchart.zendesk.com/hc/en-us',
  es: 'https://lucidchart.zendesk.com/hc/es-419',
  'pt-br': 'https://lucidchart.zendesk.com/hc/pt',
  fr: 'https://lucidchart.zendesk.com/hc/fr',
  de: 'https://lucidchart.zendesk.com/hc/de',
  nl: 'https://lucidchart.zendesk.com/hc/nl',
  ja: 'https://lucidchart.zendesk.com/hc/ja'
};

const supportLinks: { [key: string]: string } = {
  en:
    'https://lucidchart.zendesk.com/hc/en-us/articles/213497983-Contact-Support',
  es:
    'https://lucidchart.zendesk.com/hc/es-419/articles/213497983-Contactar-a-Soporte',
  'pt-br':
    'https://lucidchart.zendesk.com/hc/pt/articles/213497983-Entre-em-contato-com-o-suporte',
  fr:
    'https://lucidchart.zendesk.com/hc/fr/articles/213497983-Contactez-l-assistance',
  de:
    'https://lucidchart.zendesk.com/hc/de/articles/213497983-Kundendienst-kontaktieren',
  nl:
    'https://lucidchart.zendesk.com/hc/nl/articles/213497983-Neem-contact-op-met-ondersteuning',
  ja:
    'https://lucidchart.zendesk.com/hc/ja/articles/213497983-%E3%82%B5%E3%83%9D%E3%83%BC%E3%83%88%E3%82%BB%E3%83%B3%E3%82%BF%E3%83%BC%E3%81%AB%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B%E3%82%8B'
};

const SingleMarketoForm = ({ data, pageContext, location }: TemplateProps) => {
  const { i18nData, drupal } = data;
  const node = drupal.nodeById as Drupal_NodeSingleMarketoForm;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const {
    langPrefix,
    pageType,
    nid,
    extraData,
    i18nKey,
    hideSsr
  } = pageContext;
  const { marketoForms } = JSON.parse(extraData) as ExtraData;

  const i18n = i18nFunct(i18nData, i18nKey);

  const customers = drupal.nodeQuery!.entities as Drupal_NodeCaseStudy[];

  const customersButton = data.drupal.nodeReference;
  const customersButtonPath = customersButton?.entityUrl?.path;

  const navbarMenu = useMenuSingleMarketoFormPageMe(langPrefix);

  const helpCenterLink = helpCenterLinks[langPrefix] || helpCenterLinks.en;
  const supportLink = supportLinks[langPrefix] || supportLinks.en;
  const marketoFormFields = node.fieldMarketoForm?.entity as Drupal_ParagraphMarketoForm;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      navbar={{menu: navbarMenu}}
      nid={nid}
      pageType={pageType}
    >
      <Box css={backgroundGrayPattern}>
        <LucidContainer>
          <Box
            css={{
              display: 'flex',
              flexFlow: 'row nowrap',
              justifyContent: 'space-between',
              [mqMax.tabletLandscape]: {
                flexFlow: 'column nowrap'
              }
            }}
          >
            <Box
              flex={{
                default: '0 1 45%',
                computer: '0 1 48%',
                tabletLandscape: '0 1 50%'
              }}
              margin={{ default: '3rem 0', tabletLandscape: '3rem' }}
            >
              <Paper
                css={{
                  [mqMax.mobile]: {
                    border: '0',
                    padding: '0',
                    boxShadow: 'none',
                    background: 'none',
                    overflow: 'visible'
                  }
                }}
                variant="raised"
              >
                <MarketoForm
                  fullWidth
                  fullWidthSubmit
                  showTerms
                  useReferrer
                  form={getMarketoForm(marketoForms, node.fieldMarketoForm)!}
                  i18nKey={i18nKey}
                  location={location}
                  showLabels={false}
                  submitText={i18n('mktg-generics-forms-submit')}
                  topText={{
                    body:`<h2 style="margin-top:0;">${marketoFormFields?.fieldMarketoTitle || i18n('mktg-form-page-h1')}</h2>
                      <p>${marketoFormFields.fieldMarketoBody?.processed || i18n('mktg-form-page-body')}</p>`
                  }}
                >
                  <Text>{i18n('mktg-form-page-thanks-body')}</Text>
                  {customersButtonPath && (
                    <Link
                      css={(theme: LucidTheme) => ({
                        color: theme.colors.orange[45],
                        '&:visited': {
                          color: theme.colors.orange[45]
                        },
                        '&::after': {
                          content: '\\2192',
                          display: 'inline-block',
                          paddingLeft: '5px',
                          fontSize: theme.fontSizes[0],
                          transition: 'transform 0.3s ease'
                        },
                        '&:hover': {
                          '&:after': {
                            transform: 'translateX(3px)'
                          }
                        }
                      })}
                      to={customersButtonPath}
                    >
                      {i18n('mktg-form-page-thanks-cta')}
                    </Link>
                  )}
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    mt="30px"
                  >
                    {customers.map(customer => (
                      <Card
                        key={customer.entityUrl!.translate!.path}
                        css={{
                          flex: '0 1 45%',
                          height: '110px',
                          position: 'relative',
                          '&:nth-last-child(-n + 2)': {
                            marginBottom: 0
                          }
                        }}
                        mb={30}
                        variant="interactive"
                      >
                        <Link to={customer.entityUrl!.translate!.path!}>
                          <Card.Image>
                            <img
                              alt={customer.title!}
                              css={{
                                maxHeight: '80%',
                                maxWidth: '80%',
                                width: 'auto',
                                margin: 'auto',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0
                              }}
                              loading="lazy"
                              src={`${customer.fieldHubLogo ||
                                customer.fieldLogo}`}
                            />
                          </Card.Image>
                        </Link>
                      </Card>
                    ))}
                  </Box>
                </MarketoForm>
              </Paper>
              <Box
                css={(theme: LucidTheme) => ({
                  borderRadius: '5px',
                  border: `1px solid ${theme.colors.coolGrey[5]}`,
                  fontSize: '90%',
                  '& a': {
                    textDecoration: 'underline'
                  }
                })}
                dangerouslySetInnerHTML={{
                  __html: i18n('mktg-form-page-help', {}, [
                    `<a href="${helpCenterLink}">{}</a>`,
                    `<a href="${supportLink}">{}</a>`
                  ])
                }}
                mt={48}
                padding={20}
              />
            </Box>
            <Box
              css={(theme: LucidTheme) => ({
                padding: '150px 0 150px 50px',
                flex: '0 1 45%',
                [mqMax.computer]: {
                  padding: '50px 0 50px 50px'
                },
                [mqMax.tabletLandscape]: {
                  padding: '50px 0',
                  flex: '0 1 100%'
                },
                background: theme.colors.coolGrey[100],
                color: 'white',
                '&::after': {
                  content: '""',
                  height: '100%',
                  width: '100%',
                  position: 'absolute',
                  left: '100%',
                  top: 0,
                  background: theme.colors.coolGrey[100],
                  [mqMax.tabletLandscape]: {
                    width: '60px',
                    right: '-60px'
                  }
                },
                '&::before': {
                  [mqMax.tabletLandscape]: {
                    content: '""',
                    height: '100%',
                    width: '60px',
                    position: 'absolute',
                    left: '-60px',
                    top: 0,
                    background: theme.colors.coolGrey[100]
                  }
                }
              })}
              display="flex"
              flexDirection="column"
              justifyContent="space-around"
              position="relative"
            >
              <Box
                css={{
                  width: '100%',
                  maxWidth: '400px',
                  margin: '0 0 0 auto',
                  [mqMax.tabletLandscape]: {
                    maxWidth: 'unset',
                    margin: 0
                  }
                }}
              >
                <Heading color="white" m={0} variant="subheadM">
                  {node.fieldSidebarText || i18n('mtkg_ppc_team_language')}
                </Heading>
                {node.fieldSidebarAttribution && (
                  <Text>{node.fieldSidebarAttribution}</Text>
                )}
              </Box>
            </Box>
          </Box>
        </LucidContainer>
      </Box>
    </Layout>
  );
};

export default SingleMarketoForm;

export const query = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
    $langPrefix: [String!]
  ) {
    drupal {
      nodeReference: nodeById(id: "996", language: $LanguageId) {
        entityUrl {
          path
        }
      }
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeSingleMarketoForm {
          ...entityFields
          fieldMarketoForm {
            entity {
              ... on Drupal_ParagraphMarketoForm {
                fieldMarketoId
                fieldMarketoBody {
                  processed
                }
                fieldMarketoTitle
              }
            }
          }
          fieldSidebarText
          fieldSidebarAttribution
        }
      }
      nodeQuery(
        limit: 6
        filter: {
          conditions: [
            { field: "type", value: ["case_study"], operator: IN }
            { field: "field_is_private", value: "0", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
        }
        sort: [
          { field: "field_weight", direction: ASC }
          { field: "field_company_name", direction: ASC }
        ]
      ) {
        entities {
          ... on Drupal_NodeCaseStudy {
            entityUrl {
              translate(language: $LanguageId) {
                path
              }
            }
            fieldCompanyName
            fieldLogo
            fieldHubLogo
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_generics_forms_submit
            mktg_form_page_thanks_body
            mktg_form_page_thanks_cta
            mtkg_ppc_team_language
            mktg_form_page_help
            mktg_form_page_h1
            mktg_form_page_body
          }
        }
      }
    }
  }
`;
